import { ApiError, checkAttendanceRegisterDateExists, DEFAULT_DATE_FORMAT_FNS } from '@schooly/api';
import { useAuth } from '@schooly/components/authentication';
import { useNotifications } from '@schooly/components/notifications';
import { getDateWithoutTime } from '@schooly/utils/date';
import { format, isAfter, isBefore, startOfToday } from 'date-fns';
import { useCallback, useMemo, useState } from 'react';
import { UseFormReturn } from 'react-hook-form-lts';
import { useIntl } from 'react-intl';

import useSchoolYears from '../../../hooks/useSchoolYears';
import { RegisterCreateStep } from './useRegisterCreateModal';
import { RegisterCreateForm } from './useRegisterComposition';
import { RegisterTab } from './types';
import { emptyGroupIds } from './utils/registerGroupIds';

type UseRegisterDateStepParams = {
  form: UseFormReturn<RegisterCreateForm>;
  isEdit: boolean;
  setStep: (step: RegisterCreateStep) => void;
  setHasManualSelection: (value: boolean) => void;
  setSelectedTab: (value: RegisterTab | undefined) => void;
};

export const useRegisterDateStep = ({
  form,
  isEdit,
  setStep,
  setHasManualSelection,
  setSelectedTab,
}: UseRegisterDateStepParams) => {
  const { formatMessage } = useIntl();
  const { schoolId = '' } = useAuth();
  const { showError } = useNotifications();
  const { getSchoolYearByDate, getDefaultValidity } = useSchoolYears();
  const [checkingDate, setCheckingDate] = useState(false);

  const date = form.watch('register_date');
  const today = useMemo(() => startOfToday(), []);

  const currentSchoolYear = useMemo(
    () => getSchoolYearByDate(today) ?? getDefaultValidity(),
    [getSchoolYearByDate, getDefaultValidity, today],
  );

  const minDate = useMemo(
    () => (currentSchoolYear ? getDateWithoutTime(currentSchoolYear.start) : undefined),
    [currentSchoolYear],
  );

  const onDateChange = useCallback(
    (value: Date) => {
      if (isEdit) return;

      const isBeforeSchoolYear = !!minDate && isBefore(value, minDate);
      const isInFuture = isAfter(value, today);
      const isOutOfRange = isBeforeSchoolYear || isInFuture;

      const fallbackDate = date ? getDateWithoutTime(date) : today;
      const nextDate = isOutOfRange ? fallbackDate : value;

      form.clearErrors('register_date');
      form.setValue('register_date', format(nextDate, DEFAULT_DATE_FORMAT_FNS), {
        shouldDirty: true,
        shouldValidate: true,
      });
      form.setValue('group_ids', emptyGroupIds(), { shouldDirty: true });
      setSelectedTab(undefined);
      setHasManualSelection(false);
    },
    [form, date, minDate, today, isEdit, setHasManualSelection, setSelectedTab],
  );

  const goToGroups = useCallback(async () => {
    if (!schoolId || isEdit) return;

    if (!date) {
      form.setError('register_date', {
        type: 'required',
        message: formatMessage({ id: 'input-ErrorRequired' }),
      });
      return;
    }

    setCheckingDate(true);
    form.clearErrors('register_date');

    try {
      const registerExists = await checkAttendanceRegisterDateExists({
        schoolId,
        registerDate: date,
      });

      if (registerExists) {
        form.setError('register_date', {
          type: 'validate',
          message: formatMessage({ id: 'attendance-DateExistsError' }),
        });
        return;
      }

      setStep('groups');
    } catch (error) {
      showError(error as ApiError);
    } finally {
      setCheckingDate(false);
    }
  }, [date, form, formatMessage, schoolId, showError, isEdit, setStep]);

  const goToDate = useCallback(() => {
    if (isEdit) return;
    setStep('date');
  }, [isEdit, setStep]);

  return {
    date,
    minDate,
    maxDate: today,
    checkingDate,
    errors: form.formState.errors,
    onDateChange,
    goToGroups,
    goToDate,
  };
};
